/* ============================================================
   埋点 — 神策 Web SDK 的唯一入口(口径文档:docs/tracking-web.md)。

   规矩:
   · 业务代码只 import 这里的 track()/trackPage()/trackLogin()/trackLogout(),
     永远不直接碰 sensors —— SDK 挂了埋点降级,业务不许受伤;
   · mock 模式(VITE_MOCK=1)不初始化 SDK、不发网络请求:合成数据
     不进库,事件进 console + window.__track(冒烟断言用);
   · 上报地址走 VITE_SA_URL(owner 2026-08-28:线上 project=miya,
     开发/本地不配即默认 acestudio),非 miya.fm 域名多开 show_log;
   · window.__track 两种模式都记,线上排查随手可用。

   SDK 为自托管文件(src/api/vendor/,零 npm 依赖),来源见其文件头。
   ============================================================ */
import { ENV } from './env.js';
import { HANDHELD } from '@/utils/util.js';
/* the SDK (206 KB, js/vendor/) is loaded LAZILY (perf 2026-09-22): a static import put it in the chunk every page
   modulepreloads, ahead of the page's own first request — the song share page waited on 59 KB of analytics before asking
   for the song. Now initTrack() pulls it after `load`; until it lands, events queue here and replay in order. */
let sensors = null; /* the SDK's default export once loaded and init()ed */
const queued = []; /* [fn(sdk)] — events that arrived before the SDK */

const SERVER_URL =
  ENV.SA_URL || 'https://timedomaintech.datasink.sensorsdata.cn/sa?project=acestudio&token=8c70e09870d64df8';
const REAL = !ENV.MOCK; /* mock 合成数据不上报 */
const NOISY = location.hostname !== 'miya.fm'; /* 开发环境多控制台打印 */

let uid = ''; /* mia-data user_id,登录后有值;公共属性按 track 时取 */
let inited = false;

/* os / os_version:神策预置有 $os,但表格要求与客户端同名的自定义字段 */
function uaOS() {
  const ua = navigator.userAgent;
  let m;
  if ((m = ua.match(/Windows NT ([\d.]+)/))) return ['Windows', m[1]];
  if ((m = ua.match(/(?:iPhone|iPad).*? OS ([\d_]+)/))) return ['iOS', m[1].replace(/_/g, '.')];
  if ((m = ua.match(/Mac OS X ([\d_.]+)/))) return ['macOS', m[1].replace(/_/g, '.')];
  if ((m = ua.match(/Android ([\d.]+)/))) return ['Android', m[1]];
  if (/Linux/.test(ua)) return ['Linux', ''];
  return ['Other', ''];
}

/* page:本次加载算哪个页面的 $pageview —— 'home' | 'share' | 'song' | 'listen'
   (owner 2026-08-29:三处都报)。$url/$title/$referrer 是 SDK 自动带的,
   page 是我们给分析用的稳定业务名:$title 文案会改,/videos/{id} 的
   $url_path 条条不同,都当不了分组维度。不传 page 就不发 $pageview。 */
/* ONE DOCUMENT, SEVERAL PAGES. mia-web-back loaded a document per page, so `initTrack(page)` sent that page's one
   $pageview and was done. The SPA initialises ONCE and then changes page without a load — so the first route
   inits and every later one reports its own view through `trackPage` (router.afterEach). Until 2026-09-26
   nothing called initTrack at all: `track()` sends only when `inited`, so every event on every page was
   dropped in silence, $pageview included. */
let pendingPage = '';
export function trackPage(page) {
  if (!inited) return initTrack(page);
  if (!page) return;
  try {
    (window.__track = window.__track || []).push({ event: '$pageview', props: { page } });
    if (NOISY) console.info('[track] $pageview', page);
    if (!REAL) return;
    if (sensors) sensors.quick('autoTrack', { page });
    else pendingPage = page; /* the SDK is still loading: the latest page is the one it reports on arrival */
  } catch {}
}
export function initTrack(page) {
  if (inited) return;
  inited = true;
  const pv = () => {
    page = pendingPage || page;
    if (!page) return;
    try {
      (window.__track = window.__track || []).push({ event: '$pageview', props: { page } });
      if (NOISY) console.info('[track] $pageview', page);
      if (REAL) sensors.quick('autoTrack', { page });
    } catch {}
  };
  if (!REAL) {
    if (NOISY) console.info('[track] mock mode — events land in window.__track only');
    pv();
    return;
  }
  const start = () =>
    import('@/api/vendor/sensorsdata.es6.min.js')
      .then((m) => {
        setup(m.default);
        pv();
        for (const fn of queued.splice(0)) {
          try {
            fn(sensors);
          } catch {}
        }
      })
      .catch((e) => console.error('[track] sdk failed to load — tracking disabled', e));
  /* after the page's own work: the SDK is the least urgent byte on any page */
  if (document.readyState === 'complete') setTimeout(start, 0);
  else addEventListener('load', () => setTimeout(start, 0), { once: true });
}

function setup(sdk) {
  try {
    sensors = sdk;
    sensors.init({
      server_url: SERVER_URL,
      show_log: NOISY,
      send_type: 'beacon' /* 离开页面前的事件(如登出后的整页刷新)也送得出去 */,
      heatmap: { clickmap: 'not_collect', scroll_notice_map: 'not_collect' } /* 全部手动埋点 */,
    });
    const [os, os_version] = uaOS();
    /* platform:访客用的是什么设备——mobile = 手机/平板(主输入是手指,util.js HANDHELD),desktop = 电脑。
       问的是"这台设备能不能装 Miya 桌面版",不是"看到哪套布局":窗口宽窄不算(窄的桌面窗口仍是 desktop),
       和分享页挑门(Send Miya to my computer)是同一条判定。2026-09-23 前是"hover:none 或 ≤700px",
       窄桌面窗口曾被记成 mobile——跨这天比数要注意口径变了。设备细节用预置 $os 交叉。 */
    const platform = HANDHELD ? 'mobile' : 'desktop';
    sensors.registerPage({
      platform,
      uid: () => uid,
      device_id: () => {
        try {
          return sensors.quick('getAnonymousID') || '';
        } catch {
          return '';
        }
      },
      app_version: 'web' /* owner 2026-08-27:web 端先用固定值 */,
      os,
      os_version,
    });
    /* 点击自动采集与热力图保持全关(owner 2026-08-28);$pageview 例外,
       initTrack 的 quick('autoTrack') 只发这一条,不会顺带打开点击采集 */
  } catch (e) {
    sensors = null;
    console.error('[track] init failed — tracking disabled', e);
  }
}

export function track(event, props = {}) {
  try {
    (window.__track = window.__track || []).push({ event, props });
    if (NOISY) console.info('[track]', event, JSON.stringify(props));
    if (REAL && inited) {
      if (sensors) sensors.track(event, props);
      else queued.push((s) => s.track(event, props));
    }
  } catch (e) {
    if (NOISY) console.error('[track] drop', event, e);
  }
}

/* 登录态 ↔ 神策身份:setUser 调 trackLogin,signout 调 trackLogout。
   uid 同时落一份用户属性(表:用户属性.uid)。 */
export function trackLogin(userId) {
  uid = String(userId || '');
  if (!REAL || !inited || !uid) return;
  const login = (s, id) => {
    s.login(id);
    s.setOnceProfile({ uid: id });
  };
  try {
    if (sensors) login(sensors, uid);
    else {
      const id = uid;
      queued.push((s) => login(s, id));
    }
  } catch {}
}
export function trackLogout() {
  uid = '';
  if (REAL && inited)
    try {
      if (sensors) sensors.logout();
      else queued.push((s) => s.logout());
    } catch {}
}
